<template>
  <div class="w-full h-full flex flex-col bg-gray-50 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
    <!-- 🌀 Top-level loader (page still visible underneath) -->
    <transition name="fade">
      <div v-if="isLoading" class="absolute inset-0 bg-white/75 backdrop-blur-sm flex flex-col items-center justify-center z-40">
        <div class="h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-600 font-medium">{{ loadingText }}</p>
      </div>
    </transition>

    <!-- ❗ Runtime error banner (never hides UI) -->
    <transition name="fade">
      <div
        v-if="errorMsg"
        class="absolute top-3 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow bg-red-100 text-red-700 border border-red-200 text-sm"
      >
        {{ errorMsg }}
      </div>
    </transition>

    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 shadow-sm z-10">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-md">
          <i class="ri-robot-2-line text-xl"></i>
        </div>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">AI Assistant</h2>
          <p class="text-xs text-gray-500">Ask insights about your website analytics</p>
        </div>
      </div>

      <!-- Website Selector (always visible) -->
      <div>
        <select
          v-model="selectedWebsite"
          @change="changeWebsite"
          class="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option disabled value="">Select Website</option>
          <option v-for="site in websites" :key="site.web_id" :value="toKey(site.web_id)">
            {{ site.web_link }}
          </option>
        </select>
      </div>
    </div>

    <!-- Chat Window (always visible) -->
    <div ref="chatBody" class="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-gray-50">
      <!-- Empty state before selection -->
      <div v-if="!selectedWebsite && !loadingMessages && !messages.length && !isTyping" class="text-center text-gray-400 mt-16">
        <i class="ri-chat-smile-2-line text-4xl mb-3 text-blue-400"></i>
        <div class="text-sm">Select a website to start your conversation with AI.</div>
      </div>

      <!-- Loading conversation -->
      <div v-if="loadingMessages" class="flex flex-col items-center mt-16 text-gray-500">
        <div class="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
        <div class="text-sm">Loading conversation...</div>
      </div>

      <!-- No messages yet -->
      <div v-if="selectedWebsite && !loadingMessages && !messages.length" class="text-center text-gray-400 italic mt-12">
        No messages yet. Ask your first question 👇
      </div>

      <!-- Messages -->
      <div
        v-for="(msg, index) in messages"
        :key="index"
        :class="['flex items-start gap-3 animate-fadeIn', msg.sender === 'Bot' ? 'justify-end' : 'justify-start']"
      >
        <!-- AI -->
        <div v-if="msg.sender !== 'Bot'" class="flex-shrink-0">
          <div class="w-9 h-9 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-sm">
            <i class="ri-robot-2-line"></i>
          </div>
        </div>

        <!-- Bubble -->
        <div
          :class="[
            'max-w-[75%] px-4 py-2 text-sm rounded-2xl shadow-sm break-words whitespace-pre-wrap',
            msg.sender === 'Bot'
              ? 'bg-blue-600 text-white rounded-br-none'
              : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
          ]"
          v-html="msg.sender !== 'Bot' ? formatAiResponse(msg.text,msg.sender) : msg.text"
        ></div>

        <!-- User -->
        <div v-if="msg.sender === 'Bot'" class="flex-shrink-0">
          <div class="w-9 h-9 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center shadow-sm">
            <i class="ri-user-3-line"></i>
          </div>
        </div>
      </div>

      <!-- Typing indicator -->
      <div v-if="isTyping" class="flex items-center gap-2 mt-2 text-gray-500">
        <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-sm">
          <i class="ri-robot-2-line"></i>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
          <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: .15s"></span>
          <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: .3s"></span>
        </div>
      </div>
    </div>

    <!-- Input Bar (always enabled UI; input disables when needed) -->
    <div class="bg-white border-t border-gray-100 px-6 py-4 flex items-center gap-3 shadow-inner">
      <input
        v-model="userMessage"
        type="text"
        placeholder="Ask AI about your analytics..."
        class="flex-1 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        @keyup.enter="sendMessage"
        :disabled="sending || !selectedWebsite"
      />
      <button
        @click="sendMessage"
        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-sm transition disabled:opacity-50"
        :disabled="sending || !userMessage.trim() || !selectedWebsite"
      >
        <i v-if="!sending" class="ri-send-plane-2-line text-lg"></i>
        <i v-else class="ri-loader-4-line text-lg animate-spin"></i>
      </button>
    </div>
  </div>
</template>

<script>
import {
  getWebsiteList,
  createConversation,
  getLastConversation,
  getMessages,
  sendMessage as sendMessageApi,
  getAiMessage,
  getDashboardReport
} from "@/apis/dashapis";

export default {
  name: "AIAssistant",
  data() {
    return {
      ldata: null,
      report_dt: {},
      websites: [],
      selectedWebsite: "",
      convId: null,
      messages: [],
      userMessage: "",
      isTyping: false,
      loadingMessages: false,
      sending: false,
      isLoading: true,
      loadingText: "Initializing assistant...",
      errorMsg: "",
    };
  },
  async mounted() {
    try {
      const raw = sessionStorage.getItem("ldata");
      this.ldata = raw ? JSON.parse(raw) : null;
      if (!this.ldata) {
        this.warn("No session found. UI remains available, but API calls are skipped.");
      } else {
        await this.loadWebsites();
        // Optional: auto-select first site if you want instant readiness
        if (this.websites.length && !this.selectedWebsite) {
          this.selectedWebsite = this.toKey(this.websites[0].web_id);
          
          await this.changeWebsite();

          
        }
      }
    } catch (err) {
      this.fail("Initialization error. Check console.");
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    
    toKey(v) {
      // Normalize id to string to avoid v-model equality issues (number vs string)
      return v != null ? String(v) : "";
    },
    show(msg, type = "info") {
      this.errorMsg = type === "danger" ? msg : "";
      // (You can expand toasts for non-error types if you want)
      if (this.errorMsg) setTimeout(() => (this.errorMsg = ""), 3500);
    },
    warn(msg) {
      console.warn(msg);
      this.show(msg, "info");
    },
    fail(msg) {
      console.error(msg);
      this.show(msg, "danger");
    },

    async loadWebsites() {
      try {
        this.loadingText = "Loading your websites...";
        const res = await getWebsiteList(this.ldata.user_id);
        if (res?.status && Array.isArray(res.records)) {
          this.websites = res.records;
        } else {
          this.warn("No websites found.");
        }
      } catch (e) {
        this.fail("Failed to load websites.");
        console.error(e);
      }
    },

    async changeWebsite() {
      // reset conversation state
      this.messages = [];
      this.convId = null;
      this.userMessage = "";
      if (!this.selectedWebsite || !this.ldata) return;

      this.loadingMessages = true;
      try {
        const webId = this.selectedWebsite; // already normalized to string
        const lastConv = await getLastConversation(this.ldata.user_id, webId);

        if (lastConv?.status && lastConv.conv_dt) {
          this.convId = lastConv.conv_dt.conv_id;
          await this.loadMessages();
        } else {
          const created = await createConversation(this.ldata.user_id, webId);
          if (created?.status && created.conv_dt) {
            this.convId = created.conv_dt.conv_id;
            // No messages yet; UI still shows "No messages" state
          }
        }
      } catch (e) {
        this.fail("Failed to initialize conversation.");
        console.error(e);
      } finally {
        this.loadingMessages = false;
      }
       try {
        this.loadingText = "Fetching website details...";
        this.isLoading = true;
        const res = await getDashboardReport(
          this.selectedWebsite,
          "",
          "",
          ""
        );
        this.isLoading = false;
        if (res.status && res.report_dt) {
          const data = res.report_dt;
          this.report_dt = data;
        }
      } catch (e) {
        this.isLoading = false;
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    async loadMessages() {
      if (!this.convId || !this.ldata) return;
      this.loadingMessages = true;
      try {
        const res = await getMessages(this.convId, this.ldata.user_id, 0);
        const rows = Array.isArray(res?.records) ? res.records : [];
        // Robust sender mapping (handles User/Bot/user_id/name)
         this.messages = rows.map((m) => ({
            sender: m.msg_user1 === "Bot" ? "Bot" : "user",
            text: m.msg_txt || "",
          }));
      } catch (e) {
        this.fail("Failed to load messages.");
        console.error(e);
      } finally {
        this.loadingMessages = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    async sendMessage() {
      if (!this.userMessage.trim() || !this.convId || !this.ldata) return;
      const text = this.userMessage.trim();

      this.messages.push({ sender: "Bot", text });
      this.userMessage = "";
      this.scrollToBottom();

      this.sending = true;
      this.isTyping = true;

      try {
        const sent = await sendMessageApi(text, this.convId, this.ldata.user_id);
        if (sent?.status && sent.msg_dt) {
          // If your backend expects context, pass it here instead of {}
          await getAiMessage(sent.msg_dt.msg_id, this.report_dt);
          await this.loadMessages();
        }
      } catch (e) {
        this.fail("Failed to send message.");
        console.error(e);
      } finally {
        this.sending = false;
        this.isTyping = false;
        this.scrollToBottom();
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatBody;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

     formatAiResponse(rawText) {
        if (!rawText) return "";

        // Basic cleanup
        let formatted = rawText
         .replace(/###\s?(.*?)(\n|$)/g, "<h5 style='font-weight:600;margin-top:0.75rem;'>$1</h5>")
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold
          .replace(/Summary:/gi, "<h4 style='font-weight:600;margin-top:0.5rem;'>📊 Summary</h4>")
          .replace(/Insights:/gi, "<h4 style='font-weight:600;margin-top:0.75rem;'>💡 Insights</h4>")
          .replace(/Recommendations:/gi, "<h4 style='font-weight:600;margin-top:0.75rem;'>🧭 Recommendations</h4>")
          .replace(/(\d+)\.\s/g, "<br><span style='margin-left:0.75rem;'>$1.</span> ") // Numbered lists
          .replace(/-\s/g, "<br>• ") // Bullet points
          .replace(/\n+/g, "<br>")
          .replace(/##\s?(.*?)\n/g, "<h4>$1</h4>")
          .replace(/\*\s(.*?)\n/g, "• $1<br>"); // Handle newlines

        // Wrap in paragraph styling for readability
        return `<div style="
          line-height:1.5;
          font-size:0.875rem;
          color:#1f2937;
          white-space:pre-wrap;
        ">${formatted}</div>`;
      },

  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active { transition: opacity .25s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

.animate-fadeIn { animation: fadeIn .3s ease-out; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
